import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Send, Mic, MicOff, Globe, ChevronDown, Check, Bug } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { oaklandLanguages } from '../language/languages.js';
import VoiceDebugWindow from './VoiceDebugWindow';

const ChatInput = ({ 
  inputMessage, 
  setInputMessage, 
  isLoading, 
  onSendMessage,
  enableAutoRecording = false,
  onVoiceStateChange,
  ttsState,
  onInputModeChange,
  onMarkMessagesAsRead
}) => {
  const { theme, currentThemeName } = useTheme();
  const { currentLanguage, changeLanguage, t } = useLanguage();
  
  // Voice recording state - SIMPLIFIED
  const [isRecording, setIsRecording] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [soundLevel, setSoundLevel] = useState(0);
  const [isAutoRecording, setIsAutoRecording] = useState(false);
  const [hasUserSpoken, setHasUserSpoken] = useState(false);
  const [isAutoDisabled, setIsAutoDisabled] = useState(false); // User manually disabled auto-recording
  const [hasStartedManually, setHasStartedManually] = useState(false); // User has clicked mic at least once
  const [showLanguageSelector, setShowLanguageSelector] = useState(false); // Show language selector
  const [inputMode, setInputMode] = useState('voice'); // 'voice' or 'keyboard'
  const [safariInitialized, setSafariInitialized] = useState(false); // Safari audio context initialization
  const [safariStream, setSafariStream] = useState(null); // Safari-specific stream management
  const [showVoiceDebug, setShowVoiceDebug] = useState(false); // Voice debug window
  const [isNarrowViewport, setIsNarrowViewport] = useState(typeof window !== 'undefined' && window.innerWidth < 768);
  
  // Refs for voice recognition and audio analysis
  const recognitionRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const microphoneRef = useRef(null);
  const soundLevelIntervalRef = useRef(null);
  const silenceTimeoutRef = useRef(null);
  const autoStartTimeoutRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);

  // Use short placeholder on narrow viewports to avoid truncation
  useEffect(() => {
    const onResize = () => setIsNarrowViewport(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (onSendMessage && inputMessage && inputMessage.trim()) {
      onSendMessage(inputMessage);
      setInputMessage('');
      // Reset auto-recording state for next cycle
      setIsAutoRecording(false);
      setHasUserSpoken(false);
      // Re-enable auto-recording for next cycle
      setIsAutoDisabled(false);
    }
  };

  // Safari-specific initialization function - Enhanced for deep Safari issues
  const initializeSafariAudio = async () => {
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    
    if ((isSafari || isIOS) && !safariInitialized) {
      try {
        console.log('Safari: Deep initialization for microphone access');
        
        // Safari requires multiple steps for reliable audio access
        // Step 1: Create and immediately resume AudioContext
        const AudioContextClass = window.AudioContext || window.webkitAudioContext;
        const tempContext = new AudioContextClass();
        
        // Step 2: Force resume AudioContext (Safari requirement)
        if (tempContext.state === 'suspended') {
          await tempContext.resume();
          console.log('Safari: AudioContext resumed');
        }
        
        // Step 3: Test microphone with Safari-optimized constraints
        const stream = await navigator.mediaDevices.getUserMedia({ 
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            sampleRate: 44100,
            channelCount: 1,
            latency: 0.01, // Low latency for Safari
            volume: 1.0
          } 
        });
        
        console.log('Safari: Microphone stream obtained');
        
        // Step 4: Create analyser to test audio processing
        const analyser = tempContext.createAnalyser();
        const source = tempContext.createMediaStreamSource(stream);
        source.connect(analyser);
        
        // Step 5: Test audio processing (Safari needs this)
        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);
        analyser.getByteFrequencyData(dataArray);
        
        // Step 6: Clean up test resources
        source.disconnect();
        stream.getTracks().forEach(track => {
          track.stop();
          track.enabled = false;
        });
        tempContext.close();
        
        setSafariInitialized(true);
        console.log('Safari: Deep audio initialization completed successfully');
      } catch (error) {
        console.warn('Safari: Deep audio initialization failed:', error);
        
        // Safari-specific error handling
        if (error.name === 'NotAllowedError') {
          console.error('Safari: Microphone permission denied. User must allow microphone access.');
        } else if (error.name === 'NotFoundError') {
          console.error('Safari: No microphone found. Please connect a microphone.');
        } else if (error.name === 'NotReadableError') {
          console.error('Safari: Microphone is being used by another application.');
        }
        
        // Continue anyway, speech recognition might still work
        setSafariInitialized(true); // Mark as attempted
      }
    }
  };

  // Create speech recognition instance - Safari compatible
  const createSpeechRecognition = () => {
    // Check for Safari-specific issues
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new SpeechRecognition();
      
      // Safari-specific configurations
      if (isSafari || isIOS) {
        recognition.continuous = false; // Safari works better with non-continuous
        recognition.interimResults = true;
        recognition.maxAlternatives = 1; // Reduce alternatives for better performance
        
        // Safari-specific: Try to bypass service restrictions
        try {
          recognition.serviceURI = 'wss://www.google.com/speech-api/v2/recognize';
        } catch (e) {
          console.log('Safari: Could not set service URI, using default');
        }
        
        // Safari-specific: Set additional properties that might help
        try {
          recognition.grammars = null; // Clear any existing grammars
        } catch (e) {
          console.log('Safari: Could not clear grammars');
        }
      } else {
        recognition.continuous = true;
        recognition.interimResults = true;
      }
      
      recognition.lang = currentLanguage || 'en-US';

      recognition.onstart = () => {
        console.log('Voice recognition started', isSafari ? '(Safari mode)' : '');
      };

      recognition.onresult = (event) => {
        let finalTranscript = '';
        let interimTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          } else {
            interimTranscript += transcript;
          }
        }

        const currentTranscript = finalTranscript || interimTranscript;
        setTranscript(currentTranscript);
        setInputMessage(currentTranscript);
        
        // Mark that user has spoken
        if (currentTranscript.trim()) {
          setHasUserSpoken(true);
          console.log('Speech detected:', currentTranscript);
        }
      };

      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        
        // Safari-specific error handling
        if (isSafari && event.error === 'not-allowed') {
          console.warn('Safari: Microphone permission denied. Please allow microphone access.');
          alert('Please allow microphone access in Safari settings to use voice chat.');
        } else if (isSafari && event.error === 'no-speech') {
          console.warn('Safari: No speech detected. This is common in Safari.');
          // Don't stop recording immediately in Safari, give more time
          return;
        } else if (isSafari && event.error === 'service-not-allowed') {
          console.warn('Safari: Speech recognition service blocked. Trying alternative approach...');
          // Safari blocks speech recognition service, try to restart with different settings
          setTimeout(() => {
            if (isRecording && recognitionRef.current) {
              try {
                // Create new recognition instance for Safari
                const newRecognition = createSpeechRecognition();
                if (newRecognition) {
                  recognitionRef.current = newRecognition;
                  newRecognition.start();
                  console.log('Safari: Speech recognition restarted with new instance');
                }
              } catch (restartError) {
                console.error('Safari: Failed to restart speech recognition:', restartError);
                // Fall back to manual input mode
                alert('Safari speech recognition is blocked. Please use keyboard input instead.');
                stopRecording();
              }
            }
          }, 1000);
          return; // Don't stop recording, try to recover
        }
        
        stopRecording();
      };

      recognition.onend = () => {
        console.log('Voice recognition ended');
        
        // Safari-specific: Enhanced restart logic for opening/closing issues
        if (isSafari && isRecording) {
          console.log('Safari: Handling recognition end - checking for restart');
          
          // Safari often ends recognition prematurely, need to restart
          if (!hasUserSpoken) {
            console.log('Safari: No speech detected, restarting recognition');
            setTimeout(() => {
              if (isRecording && recognitionRef.current) {
                try {
                  recognitionRef.current.start();
                  console.log('Safari: Recognition restarted successfully');
                } catch (error) {
                  console.error('Safari: Failed to restart recognition:', error);
                  // If restart fails, stop recording
                  stopRecording();
                }
              }
            }, 200); // Slightly longer delay for Safari
          } else {
            console.log('Safari: Speech detected, processing result');
            // Safari sometimes ends too early even with speech, restart once more
            setTimeout(() => {
              if (isRecording && recognitionRef.current && transcript.trim()) {
                try {
                  recognitionRef.current.start();
                  console.log('Safari: Restarting after speech detection');
                } catch (error) {
                  console.log('Safari: Recognition restart not needed, processing speech');
                }
              }
            }, 100);
          }
        }
      };

      return recognition;
    } else {
      console.warn('Speech recognition not supported in this browser');
      return null;
    }
  };

  // Safari-specific fallback for when speech recognition is blocked
  const startSafariFallback = async () => {
    console.log('Safari: Starting fallback mode - manual input with audio monitoring');
    
    setIsRecording(true);
    setTranscript('');
    setInputMessage('');
    setHasUserSpoken(false);
    
    try {
      await startAudioAnalysis();
      console.log('Safari: Fallback mode active - audio monitoring only');
      
      // Show user that they need to type manually
      alert('Safari speech recognition is blocked. Please type your message manually. Audio monitoring is active.');
    } catch (error) {
      console.error('Safari: Fallback mode failed:', error);
      stopRecording();
    }
  };

  // Start voice recording function - Safari compatible
  const startRecording = async (isAuto = false) => {
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    
    // Initialize Safari audio on first user interaction
    if ((isSafari || isIOS) && !safariInitialized) {
      await initializeSafariAudio();
    }
    
    const recognition = createSpeechRecognition();
    if (!recognition) {
      console.warn('Speech recognition not supported in this browser');
      return;
    }
    
    try {
      setIsRecording(true);
      setTranscript('');
      setInputMessage('');
      setHasUserSpoken(false);
      setIsAutoRecording(isAuto);
      
      recognitionRef.current = recognition;
      
      // Safari-specific: Ensure user interaction before starting
      if (isSafari || isIOS) {
        console.log('Safari: Ensuring user interaction for audio access');
        // Safari requires explicit user interaction for audio
        try {
          await startAudioAnalysis();
        } catch (audioError) {
          console.warn('Safari: Audio analysis failed, continuing with speech recognition only:', audioError);
        }
      } else {
        await startAudioAnalysis();
      }
      
      recognition.start();
      console.log('Voice recording started', isAuto ? '(auto)' : '(manual)', isSafari ? '(Safari mode)' : '');
    } catch (error) {
      console.error('Error starting voice recognition:', error);
      
      // Safari-specific error handling
      if (isSafari && error.name === 'NotAllowedError') {
        alert('Safari: Please allow microphone access to use voice chat. Check Safari settings and try again.');
      } else if (isSafari && error.name === 'NotFoundError') {
        alert('Safari: No microphone found. Please connect a microphone and try again.');
      } else if (isSafari && (error.message.includes('service-not-allowed') || error.message.includes('service'))) {
        console.log('Safari: Speech recognition service blocked, starting fallback mode');
        await startSafariFallback();
        return;
      }
      
      stopRecording();
    }
  };

  // Monitor for silence - SIMPLIFIED
  useEffect(() => {
    if (isRecording) {
      // Clear any existing timeout
      if (silenceTimeoutRef.current) {
        clearTimeout(silenceTimeoutRef.current);
      }
      
      // Set timeout based on whether user has spoken
      const timeoutDuration = hasUserSpoken ? 3000 : 8000; // 3s if spoken, 8s if not
      
      silenceTimeoutRef.current = setTimeout(() => {
        if (isRecording) {
          if (transcript.trim() && hasUserSpoken) {
            console.log('Silence detected, sending message:', transcript);
            sendMessage();
          } else {
            console.log('No speech detected, stopping recording');
            stopRecording();
          }
        }
      }, timeoutDuration);
    }
    
    return () => {
      if (silenceTimeoutRef.current) {
        clearTimeout(silenceTimeoutRef.current);
      }
    };
  }, [isRecording, hasUserSpoken, transcript]);

  // Auto-start recording when AI finishes responding - SIMPLIFIED
  useEffect(() => {
    if (enableAutoRecording && 
        !isLoading && 
        !isRecording && 
        !isAutoRecording && 
        !isAutoDisabled && 
        hasStartedManually && // Only start auto after manual start
        inputMode === 'voice') { // Only start auto-recording in voice mode
      console.log('AI finished responding, starting auto-recording...');
      
      // Clear any existing timeout
      if (autoStartTimeoutRef.current) {
        clearTimeout(autoStartTimeoutRef.current);
      }
      
      // Start auto-recording after 1 second delay
      autoStartTimeoutRef.current = setTimeout(() => {
        startRecording(true); // true = isAuto
      }, 1000);
    }
    
    return () => {
      if (autoStartTimeoutRef.current) {
        clearTimeout(autoStartTimeoutRef.current);
      }
    };
  }, [isLoading, enableAutoRecording, isRecording, isAutoRecording, isAutoDisabled, hasStartedManually, inputMode]);

  // Stop recording function - Safari compatible
  const stopRecording = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    setIsRecording(false);
    setIsAutoRecording(false);
    setHasUserSpoken(false);
    stopAudioAnalysis();
    
    // Safari-specific cleanup
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    
    if ((isSafari || isIOS) && safariStream) {
      console.log('Safari: Cleaning up persistent stream');
      safariStream.getTracks().forEach(track => {
        track.stop();
        track.enabled = false;
      });
      setSafariStream(null);
    }
  };

  // Close voice chat function - disables auto-recording
  const closeVoiceChat = () => {
    console.log('User manually closed voice chat, disabling auto-recording');
    setIsAutoDisabled(true); // Disable auto-recording permanently
    setHasStartedManually(false); // Reset manual start flag
    stopRecording();
  };

  // Send message function - SIMPLIFIED
  const sendMessage = () => {
    if (transcript.trim() && onSendMessage) {
      onSendMessage(transcript.trim());
      setInputMessage('');
      setTranscript('');
      stopRecording();
    }
  };

  // Notify parent component about voice state changes
  useEffect(() => {
    if (onVoiceStateChange) {
      onVoiceStateChange({
        isRecording,
        isAutoRecording,
        hasStartedManually,
        isAutoDisabled
      });
    }
  }, [isRecording, isAutoRecording, hasStartedManually, isAutoDisabled, onVoiceStateChange]);

  // Stop recording when TTS is active and completely disable microphone
  useEffect(() => {
    if (ttsState && (ttsState.isReading || ttsState.isLoading)) {
      console.log('TTS Debug: TTS is active, completely disabling microphone');
      if (isRecording || isAutoRecording) {
        stopRecording();
      }
      // Completely disable microphone functionality during TTS
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => {
          track.enabled = false;
          track.stop();
        });
      }
    }
  }, [ttsState, isRecording, isAutoRecording]);

  // Auto-detect input mode based on user interaction - DISABLED
  // Input mode is now only changed by explicit user actions:
  // 1. Typing -> keyboard mode
  // 2. Clicking voice mic -> voice mode
  // 3. Clicking switch buttons -> respective mode
  // useEffect(() => {
  //   // If user starts voice recording, set to voice mode
  //   // Always switch to voice when mic is activated (user intent)
  //   // But only if TTS is not active (to prevent mode switch during TTS)
  //   if ((isRecording || isAutoRecording) && !(ttsState && (ttsState.isReading || ttsState.isLoading))) {
  //     console.log('TTS Debug: Voice recording detected, switching to voice mode');
  //     setInputMode('voice');
  //   }
  // }, [isRecording, isAutoRecording, ttsState]);

  // Notify parent when input mode changes
  useEffect(() => {
    if (onInputModeChange) {
      onInputModeChange(inputMode);
    }
  }, [inputMode, onInputModeChange]);

  // Detect keyboard input
  const handleInputChange = (e) => {
    setInputMessage(e.target.value);
    // If user types, set to keyboard mode and prevent voice mode switch
    if (e.target.value.length > 0) {
      console.log('TTS Debug: Keyboard input detected, switching to keyboard mode');
      setInputMode('keyboard');
    }
  };

  // Manual voice mode switch (when user clicks voice button)
  const handleVoiceModeSwitch = () => {
    console.log('TTS Debug: User manually switched to voice mode');
    setInputMode('voice');
  };

  // Restart voice mode function removed - no automatic restart after TTS

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      stopAudioAnalysis();
    };
  }, []);

  // Safari-specific stream management to handle opening/closing issues
  const manageSafariStream = async () => {
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    
    if (isSafari || isIOS) {
      try {
        // Safari needs persistent stream management
        if (!safariStream) {
          console.log('Safari: Creating persistent microphone stream');
          
          const stream = await navigator.mediaDevices.getUserMedia({
            audio: {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
              sampleRate: 44100,
              channelCount: 1,
              latency: 0.01,
              volume: 1.0
            }
          });
          
          setSafariStream(stream);
          console.log('Safari: Persistent stream created');
        }
        
        return safariStream;
      } catch (error) {
        console.error('Safari: Stream management failed:', error);
        return null;
      }
    }
    
    return null;
  };

  // Start audio analysis for sound level monitoring - Safari compatible
  const startAudioAnalysis = async () => {
    try {
      console.log('Starting audio analysis...');
      
      // Safari-specific audio constraints
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      
      let stream;
      
      if (isSafari || isIOS) {
        // Use Safari stream management
        stream = await manageSafariStream();
        if (!stream) {
          console.warn('Safari: Using fallback stream creation');
          stream = await navigator.mediaDevices.getUserMedia({
            audio: {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
              sampleRate: 44100,
              channelCount: 1
            }
          });
        }
      } else {
        // Standard Chrome/Firefox handling
        stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: false,
            noiseSuppression: false,
            autoGainControl: false
          }
        });
      }
      
      console.log('Microphone stream obtained:', stream);
      
      // Safari-specific AudioContext handling
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      audioContextRef.current = new AudioContextClass();
      
      // Safari requires user interaction to resume AudioContext
      if (audioContextRef.current.state === 'suspended') {
        try {
          await audioContextRef.current.resume();
          console.log('Audio context resumed');
        } catch (error) {
          console.warn('Safari: Could not resume AudioContext:', error);
          // Continue anyway, Safari might work without explicit resume
        }
      }
      
      analyserRef.current = audioContextRef.current.createAnalyser();
      microphoneRef.current = audioContextRef.current.createMediaStreamSource(stream);
      
      // Safari-specific analyser configuration
      if (isSafari || isIOS) {
        analyserRef.current.fftSize = 512; // Larger FFT for Safari
        analyserRef.current.smoothingTimeConstant = 0.3; // More smoothing for Safari
        analyserRef.current.minDecibels = -90;
        analyserRef.current.maxDecibels = -10;
      } else {
        analyserRef.current.fftSize = 256;
        analyserRef.current.smoothingTimeConstant = 0.1;
        analyserRef.current.minDecibels = -100;
        analyserRef.current.maxDecibels = 0;
      }
      
      microphoneRef.current.connect(analyserRef.current);
      
      console.log('Audio analyser connected', isSafari ? '(Safari mode)' : '');
      
      const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
      const timeDomainArray = new Uint8Array(analyserRef.current.frequencyBinCount);
      
      let lastSoundLevel = 0;
      
      const updateSoundLevel = () => {
        if (analyserRef.current && isRecording) {
          // Try both frequency and time domain data
          analyserRef.current.getByteFrequencyData(dataArray);
          analyserRef.current.getByteTimeDomainData(timeDomainArray);
          
          // Method 1: Frequency domain analysis
          let sum = 0;
          for (let i = 0; i < dataArray.length; i++) {
            sum += dataArray[i] * dataArray[i];
          }
          const rms = Math.sqrt(sum / dataArray.length);
          const freqLevel = Math.min(100, (rms / 128) * 100);
          
          // Method 2: Time domain analysis (more sensitive to voice)
          let timeSum = 0;
          for (let i = 0; i < timeDomainArray.length; i++) {
            const sample = (timeDomainArray[i] - 128) / 128;
            timeSum += sample * sample;
          }
          const timeRms = Math.sqrt(timeSum / timeDomainArray.length);
          const timeLevel = Math.min(100, timeRms * 100);
          
          // Method 3: Simple average
          const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
          const avgLevel = Math.min(100, (average / 255) * 100);
          
          // Use the highest level from all methods
          const level = Math.max(freqLevel, timeLevel, avgLevel);
          
          // Add some smoothing to prevent rapid fluctuations
          const smoothedLevel = lastSoundLevel * 0.7 + level * 0.3;
          lastSoundLevel = smoothedLevel;
          
          setSoundLevel(smoothedLevel);
          
          console.log('Sound analysis:', {
            freq: freqLevel.toFixed(1),
            time: timeLevel.toFixed(1), 
            avg: avgLevel.toFixed(1),
            final: smoothedLevel.toFixed(1),
            rawData: dataArray.slice(0, 10) // First 10 values for debugging
          });
          
          // Don't auto-send based on sound level - let silence detection handle it
          // This prevents double auto-send
        }
      };
      
      soundLevelIntervalRef.current = setInterval(updateSoundLevel, 100);
      console.log('Sound level monitoring started');
      
    } catch (error) {
      console.error('Error accessing microphone:', error);
      alert('Microphone access denied. Please allow microphone access to use voice input.');
    }
  };

  // Stop audio analysis - SIMPLIFIED
  const stopAudioAnalysis = () => {
    if (soundLevelIntervalRef.current) {
      clearInterval(soundLevelIntervalRef.current);
      soundLevelIntervalRef.current = null;
    }
    
    if (silenceTimeoutRef.current) {
      clearTimeout(silenceTimeoutRef.current);
      silenceTimeoutRef.current = null;
    }
    
    if (autoStartTimeoutRef.current) {
      clearTimeout(autoStartTimeoutRef.current);
      autoStartTimeoutRef.current = null;
    }
    
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    
    if (microphoneRef.current) {
      microphoneRef.current.disconnect();
      microphoneRef.current = null;
    }
    
    setSoundLevel(0);
  };

  // Manual voice toggle - SIMPLIFIED with debug functionality
  const handleVoiceToggle = async (event) => {
    // Check if user is holding Ctrl/Cmd key for debug mode
    if (event.ctrlKey || event.metaKey) {
      console.log('Debug: Opening voice debug console');
      setShowVoiceDebug(true);
      return;
    }
    
    // Switch to voice mode when mic is clicked
    handleVoiceModeSwitch();
    
    // Mark existing messages as read to prevent auto-reading when switching to voice mode
    if (onMarkMessagesAsRead) {
      console.log('TTS Debug: Marking existing messages as read before starting voice recording');
      onMarkMessagesAsRead();
    }
    
    if (isRecording) {
      // User is manually stopping recording - disable auto-recording
      console.log('User manually stopped recording, disabling auto-recording');
      setIsAutoDisabled(true);
      stopRecording();
    } else {
      setHasStartedManually(true); // Mark that user has started manually
      setIsAutoDisabled(false); // Re-enable auto-recording when starting
      await startRecording(false); // false = manual
    }
  };

  // Language selection functions
  const handleLanguageSelect = (languageCode) => {
    changeLanguage(languageCode);
    setShowLanguageSelector(false);
  };

  const currentLanguageData = oaklandLanguages.find(lang => lang.code === currentLanguage) || oaklandLanguages[0];

  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
              transform: scale(1);
            }
            50% {
              opacity: 0.7;
              transform: scale(1.1);
            }
          }
          
          @keyframes recordingPulse {
            0%, 100% {
              box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7);
            }
            50% {
              box-shadow: 0 0 0 10px rgba(239, 68, 68, 0);
            }
          }
        `}
      </style>
      
      {/* Input Mode Switch */}
      <div style={{
        display: 'none',
        justifyContent: 'center',
        marginBottom: '12px'
      }}>
        <div style={{
          display: 'none',
          background: theme.cardBg || '#f8fafc',
          borderRadius: '25px',
          padding: '4px',
          border: `2px solid ${theme.primaryBorder}`,
          boxShadow: `0 4px 12px ${theme.primaryShadow}`,
          position: 'relative'
        }}>
          {/* Voice to Text */}
          <button
            type="button"
            onClick={() => setInputMode('voice')}
            style={{
              background: inputMode === 'voice' 
                ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)'
                : 'transparent',
              color: inputMode === 'voice' ? '#ffffff' : theme.primaryText,
              border: 'none',
              borderRadius: '20px',
              padding: '8px 16px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '600',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
              transition: 'all 0.3s ease',
              display: 'none',
              alignItems: 'center',
              gap: '6px',
              minWidth: '120px',
              justifyContent: 'center'
            }}
            onMouseEnter={e => {
              if (inputMode !== 'voice') {
                e.target.style.background = 'rgba(59, 130, 246, 0.1)';
              }
            }}
            onMouseLeave={e => {
              if (inputMode !== 'voice') {
                e.target.style.background = 'transparent';
              }
            }}
          >
            <Mic size={14} />
{t('fullchat.voiceToText')}
          </button>
          
          {/* Keyboard to Text */}
          <button
            type="button"
            onClick={() => setInputMode('keyboard')}
            style={{
              background: inputMode === 'keyboard' 
                ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)'
                : 'transparent',
              color: inputMode === 'keyboard' ? '#ffffff' : theme.primaryText,
              border: 'none',
              borderRadius: '20px',
              padding: '8px 16px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '600',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
              transition: 'all 0.3s ease',
              display: 'none',
              alignItems: 'center',
              gap: '6px',
              minWidth: '120px',
              justifyContent: 'center'
            }}
            onMouseEnter={e => {
              if (inputMode !== 'keyboard') {
                e.target.style.background = 'rgba(59, 130, 246, 0.1)';
              }
            }}
            onMouseLeave={e => {
              if (inputMode !== 'keyboard') {
                e.target.style.background = 'transparent';
              }
            }}
          >
            <span style={{ fontSize: '14px' }}>⌨️</span>
{t('fullchat.keyboardToText')}
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="chat-input-form">
        <div className="message-input-container" style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          background: theme.modalBg,
          border: `2px solid ${theme.primaryBorder}`,
          borderRadius: '50px',
          padding: '8px 8px 8px 20px',
          boxShadow: `0 8px 32px ${theme.primaryShadow}`,
          backdropFilter: 'blur(10px)',
          transition: 'all 0.3s ease',
          position: 'relative'
        }}
        onMouseEnter={e => {
          e.currentTarget.style.boxShadow = `0 12px 40px ${theme.primaryShadow}`;
          e.currentTarget.style.transform = 'translateY(-2px)';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.boxShadow = `0 8px 32px ${theme.primaryShadow}`;
          e.currentTarget.style.transform = 'translateY(0)';
        }}>
          
          {/* Voice Button */}
          <button 
            type="button" 
            className="voice-btn"
            onClick={handleVoiceToggle}
            disabled={isLoading || (ttsState && (ttsState.isReading || ttsState.isLoading))}
            style={{
              background: ttsState && (ttsState.isReading || ttsState.isLoading)
                ? 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)'
                : isRecording 
                ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                : isAutoRecording
                ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                : 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              border: 'none',
              color: 'white',
              padding: '12px',
              borderRadius: '50%',
              cursor: (!isLoading && !(ttsState && (ttsState.isReading || ttsState.isLoading))) ? 'pointer' : 'not-allowed',
              width: '44px',
              height: '44px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.3s ease',
              boxShadow: ttsState && (ttsState.isReading || ttsState.isLoading)
                ? '0 2px 8px rgba(107, 114, 128, 0.3)'
                : isRecording 
                ? '0 4px 16px rgba(239, 68, 68, 0.4)'
                : isAutoRecording
                ? '0 4px 16px rgba(245, 158, 11, 0.4)'
                : '0 2px 8px rgba(59, 130, 246, 0.3)',
              position: 'relative',
              overflow: 'hidden',
              flexShrink: 0,
              opacity: (!isLoading && !(ttsState && (ttsState.isReading || ttsState.isLoading))) ? 1 : 0.5,
              animation: isRecording ? 'recordingPulse 1.5s infinite' : isAutoRecording ? 'pulse 1s infinite' : 'none'
            }}
            onMouseEnter={e => {
              if (!isLoading && !isRecording && !(ttsState && (ttsState.isReading || ttsState.isLoading))) {
                e.currentTarget.style.transform = 'scale(1.05)';
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(59, 130, 246, 0.4)';
              }
            }}
            onMouseLeave={e => {
              if (!isLoading && !isRecording && !(ttsState && (ttsState.isReading || ttsState.isLoading))) {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(59, 130, 246, 0.3)';
              }
            }}
            title={
              ttsState && (ttsState.isReading || ttsState.isLoading)
                ? 'Microphone disabled while AI is speaking'
                : isRecording 
                ? 'Click to stop recording' 
                : enableAutoRecording 
                ? 'Auto voice input enabled - will start after AI responds' 
                : 'Click to start voice input (Ctrl+Click for debug console)'
            }
          >
            {isRecording ? <MicOff size={20} /> : <Mic size={20} />}
            {isRecording && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%)',
                animation: 'pulse 1.5s infinite',
                borderRadius: '50%'
              }} />
            )}
            {isAutoRecording && !isRecording && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'radial-gradient(circle, rgba(255,255,255,0.3) 0%, transparent 70%)',
                animation: 'pulse 1s infinite',
                borderRadius: '50%'
              }} />
            )}
          </button>

          {/* Language Selector Button - Only show when recording */}
          {(isRecording || isAutoRecording) && (
            <button
              onClick={() => setShowLanguageSelector(true)}
              disabled={isLoading}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 12px',
                background: theme.cardBg || theme.background || theme.secondaryBg || '#f8fafc',
                borderRadius: '8px',
                border: `1px solid ${theme.primaryBorder || theme.border || theme.cardBorder || theme.secondaryBorder || '#e2e8f0'}`,
                fontSize: '13px',
                fontWeight: '500',
                color: theme.secondaryText || theme.textSecondary || theme.text || theme.primaryText || '#64748b',
                cursor: !isLoading ? 'pointer' : 'not-allowed',
                transition: 'all 0.2s ease',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                opacity: !isLoading ? 1 : 0.5,
                flexShrink: 0
              }}
              onMouseEnter={e => {
                if (!isLoading) {
                  e.currentTarget.style.background = theme.cardHoverBg || theme.background || theme.secondaryBg || '#f1f5f9';
                  e.currentTarget.style.borderColor = theme.primary || theme.primaryColor || theme.accent || '#3b82f6';
                  e.currentTarget.style.color = theme.primaryText || theme.text || theme.primaryText || '#1e293b';
                }
              }}
              onMouseLeave={e => {
                if (!isLoading) {
                  e.currentTarget.style.background = theme.cardBg || theme.background || theme.secondaryBg || '#f8fafc';
                  e.currentTarget.style.borderColor = theme.primaryBorder || theme.border || theme.cardBorder || theme.secondaryBorder || '#e2e8f0';
                  e.currentTarget.style.color = theme.secondaryText || theme.textSecondary || theme.text || theme.primaryText || '#64748b';
                }
              }}
              title="Select language for voice input"
            >
              <span style={{ fontSize: '16px' }}>{currentLanguageData.flag}</span>
              <span>{currentLanguageData.native}</span>
              <ChevronDown size={12} />
            </button>
          )}

          {/* Input field - hidden during TTS */}
          {!(ttsState && (ttsState.isReading || ttsState.isLoading)) && (
            <input
              type="text"
              placeholder={
                isRecording 
                  ? t('fullchat.listening') 
                  : (isNarrowViewport ? t('fullchat.placeholderShort') : t('fullchat.placeholder'))
              }
              className="message-input"
              value={inputMessage}
              onChange={handleInputChange}
              disabled={isLoading}
              style={{
                background: 'transparent',
                border: 'none',
                color: theme.primaryText,
                outline: 'none',
                flex: 1,
                fontSize: '16px',
                padding: '12px 0',
                minWidth: 0,
                fontWeight: '400'
              }}
              onFocus={(e) => {
                e.target.parentElement.style.borderColor = theme.inputFocusBorder || theme.primary;
              }}
              onBlur={(e) => {
                e.target.parentElement.style.borderColor = theme.primaryBorder;
              }}
            />
          )}

          {/* Compact TTS indicator inside input (no full-bar orange) */}
          {ttsState && (ttsState.isReading || ttsState.isLoading) && (
            <div className="ai-speaking-badge" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              flex: 1,
              minWidth: 0,
              padding: '8px 0'
            }}>
              <span style={{
                width: '6px',
                height: '6px',
                background: 'var(--primary, #3b82f6)',
                borderRadius: '50%',
                animation: 'pulse 1.5s infinite',
                flexShrink: 0
              }} />
              <span style={{
                fontSize: '14px',
                fontWeight: '500',
                color: theme.secondaryText || theme.textSecondary || theme.primaryText,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {ttsState.isLoading ? t('fullchat.generatingAudio') : t('fullchat.aiSpeaking')}
              </span>
            </div>
          )}

          {/* Conditional button: Send or Close Voice Chat */}
          {isAutoRecording ? (
            // Close Voice Chat button (when auto recording is active)
            <button 
              type="button" 
              className="close-voice-btn"
              onClick={closeVoiceChat}
              disabled={isLoading}
              style={{
                background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                border: 'none',
                color: 'white',
                padding: '12px 16px',
                borderRadius: '25px',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                height: '44px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                transition: 'all 0.3s ease',
                opacity: isLoading ? 0.5 : 1,
                boxShadow: '0 2px 8px rgba(239, 68, 68, 0.3)',
                flexShrink: 0,
                fontSize: '14px',
                fontWeight: '600'
              }}
              onMouseEnter={e => {
                if (!isLoading) {
                  e.currentTarget.style.transform = 'scale(1.05)';
                  e.currentTarget.style.boxShadow = '0 4px 16px rgba(239, 68, 68, 0.4)';
                }
              }}
              onMouseLeave={e => {
                if (!isLoading) {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(239, 68, 68, 0.3)';
                }
              }}
            >
              <MicOff size={16} />
              Close Voice Chat
            </button>
          ) : (
            // Normal Send button
            <button 
              type="submit" 
              className="send-btn"
              disabled={isLoading || !inputMessage.trim()}
              style={{
                background: theme.primaryButton,
                border: 'none',
                color: 'white',
                padding: '12px',
                borderRadius: '50%',
                cursor: isLoading || !inputMessage.trim() ? 'not-allowed' : 'pointer',
                width: '44px',
                height: '44px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s ease',
                opacity: isLoading || !inputMessage.trim() ? 0.5 : 1,
                boxShadow: `0 2px 8px ${theme.accentShadow || 'rgba(59, 130, 246, 0.3)'}`,
                flexShrink: 0
              }}
              onMouseEnter={e => {
                if (!isLoading && inputMessage.trim()) {
                  e.currentTarget.style.transform = 'scale(1.05)';
                  e.currentTarget.style.boxShadow = `0 4px 16px ${theme.accentShadow || 'rgba(59, 130, 246, 0.3)'}`;
                }
              }}
              onMouseLeave={e => {
                if (!isLoading && inputMessage.trim()) {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = `0 2px 8px ${theme.accentShadow || 'rgba(59, 130, 246, 0.3)'}`;
                }
              }}
            >
              <Send size={18} />
            </button>
          )}

        </div>

        {/* Recording indicator - below input */}
        {(isRecording || isAutoRecording) && !ttsState?.isReading && !ttsState?.isLoading && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            marginTop: '8px',
            padding: '8px 16px',
            background: 'rgba(0, 0, 0, 0.1)',
            borderRadius: '12px',
            border: `1px solid ${isAutoRecording ? 'rgba(245, 158, 11, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`
          }}>
            {/* Recording text */}
            <div style={{
              color: isAutoRecording ? '#f59e0b' : '#ef4444',
              fontSize: '12px',
              fontWeight: '600',
              animation: 'pulse 1.5s infinite',
              whiteSpace: 'nowrap'
            }}>
              {isAutoRecording ? 'Auto Recording...' : 'Recording...'}
            </div>
            
            {/* Recording dot */}
            <div style={{
              width: '8px',
              height: '8px',
              background: isAutoRecording ? '#f59e0b' : '#ef4444',
              borderRadius: '50%',
              animation: 'pulse 1.5s infinite'
            }} />
          </div>
        )}


      </form>

      {/* Language Selector Modal */}
      {showLanguageSelector && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          backdropFilter: 'blur(12px)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}
        onClick={() => setShowLanguageSelector(false)}
        >
          <div 
            style={{
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
              border: '1px solid rgba(59, 130, 246, 0.2)',
              borderRadius: '20px',
              boxShadow: '0 25px 80px rgba(0, 0, 0, 0.4), 0 8px 32px rgba(0, 0, 0, 0.3)',
              maxWidth: '500px',
              width: '100%',
              maxHeight: '80vh',
              overflow: 'hidden',
              position: 'relative'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{
              padding: '20px 24px',
              borderBottom: '1px solid rgba(59, 130, 246, 0.2)',
              background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Globe size={20} style={{ color: 'white' }} />
                </div>
                <div>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: '700',
                    color: 'white',
                    margin: '0 0 4px 0',
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
                  }}>
                    Select Language
                  </h3>
                  <p style={{
                    fontSize: '13px',
                    color: 'rgba(255, 255, 255, 0.7)',
                    margin: 0,
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
                  }}>
                    Choose language for voice input
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowLanguageSelector(false)}
                style={{
                  width: '32px',
                  height: '32px',
                  background: 'rgba(239, 68, 68, 0.15)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: '#ef4444'
                }}
              >
                ✕
              </button>
            </div>

            {/* Current Language */}
            <div style={{
              padding: '16px 24px',
              background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(59, 130, 246, 0.03) 100%)',
              borderBottom: '1px solid rgba(59, 130, 246, 0.2)'
            }}>
              <div style={{
                fontSize: '11px',
                fontWeight: '600',
                color: 'rgba(255, 255, 255, 0.6)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: '8px'
              }}>
                Current Language
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px 16px',
                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(59, 130, 246, 0.08) 100%)',
                border: '2px solid rgba(59, 130, 246, 0.4)',
                borderRadius: '12px'
              }}>
                <span style={{ fontSize: '24px' }}>{currentLanguageData.flag}</span>
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: 'white',
                    marginBottom: '2px'
                  }}>
                    {currentLanguageData.native}
                  </div>
                  <div style={{
                    fontSize: '13px',
                    color: 'rgba(255, 255, 255, 0.7)'
                  }}>
                    {currentLanguageData.name}
                  </div>
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '24px',
                  height: '24px',
                  background: 'rgba(34, 197, 94, 0.25)',
                  borderRadius: '6px',
                  color: '#22c55e'
                }}>
                  <Check size={14} />
                </div>
              </div>
            </div>

            {/* Language List */}
            <div style={{
              maxHeight: '300px',
              overflowY: 'auto',
              padding: '16px 24px 24px 24px'
            }}>
              <div style={{
                display: 'grid',
                gap: '8px'
              }}>
                {oaklandLanguages.map((language) => (
                  <button
                    key={language.code}
                    onClick={() => handleLanguageSelect(language.code)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px 16px',
                      background: currentLanguage === language.code 
                        ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(59, 130, 246, 0.1) 100%)'
                        : 'rgba(30, 41, 59, 0.6)',
                      border: currentLanguage === language.code 
                        ? '2px solid rgba(59, 130, 246, 0.5)'
                        : '1px solid rgba(59, 130, 246, 0.1)',
                      borderRadius: '12px',
                      color: 'white',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      textAlign: 'left',
                      width: '100%',
                      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
                    }}
                    onMouseEnter={(e) => {
                      if (currentLanguage !== language.code) {
                        e.target.style.background = 'rgba(30, 41, 59, 0.8)';
                        e.target.style.borderColor = 'rgba(59, 130, 246, 0.3)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentLanguage !== language.code) {
                        e.target.style.background = 'rgba(30, 41, 59, 0.6)';
                        e.target.style.borderColor = 'rgba(59, 130, 246, 0.1)';
                      }
                    }}
                  >
                    <span style={{ fontSize: '20px' }}>{language.flag}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: 'white',
                        marginBottom: '2px'
                      }}>
                        {language.native}
                      </div>
                      <div style={{
                        fontSize: '12px',
                        color: 'rgba(255, 255, 255, 0.7)'
                      }}>
                        {language.name}
                      </div>
                    </div>
                    {currentLanguage === language.code && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '20px',
                        height: '20px',
                        background: 'rgba(34, 197, 94, 0.25)',
                        borderRadius: '4px',
                        color: '#22c55e'
                      }}>
                        <Check size={12} />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Voice Debug Window */}
      <VoiceDebugWindow
        isOpen={showVoiceDebug}
        onClose={() => setShowVoiceDebug(false)}
        theme={theme}
      />
    </>
  );
};

// CSS for forcing black text in AI Answering state and thinking animation
const style = document.createElement('style');
style.textContent = `
  .ai-speaking-text {
    color: #000000 !important;
  }
  .ai-answering-input {
    color: #000000 !important;
  }
  .ai-answering-input::placeholder {
    color: #000000 !important;
  }
  @keyframes thinkingPulse {
    0%, 100% {
      transform: scale(1);
      opacity: 1;
    }
    50% {
      transform: scale(1.1);
      opacity: 0.8;
    }
  }
  .thinking-text {
    animation: thinkingPulse 1.5s ease-in-out infinite;
    display: inline-block;
  }
`;
document.head.appendChild(style);

export default ChatInput;